import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, LINES, NEEDS } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-sm px-4 py-2 rounded-full border font-semibold transition-colors whitespace-nowrap ${active ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white border-slate-300 text-slate-700 hover:border-[#2563EB]"}`}>
    {children}
  </button>
);

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const line = params.get("line") || "";
  const need = params.get("need") || "";
  const sort = params.get("sort") || "default";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);

  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: { line: line || undefined, need: need || undefined, q: q || undefined, sort: sort === "default" ? undefined : sort } })
      .then((r) => setProducts(r.data));
  }, [line, need, q, sort]);

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    setParams(n);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="catalog-page">
      <p className="text-[#DC2626] font-bold text-sm tracking-[0.2em] uppercase">Tienda FAUNNY</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#0B1D3A] mt-2">{line ? LINES[line] : "Todos los productos"}</h1>

      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center">
        <form onSubmit={(e) => { e.preventDefault(); set("q", search); }} className="flex items-center border-2 border-slate-400 rounded-full px-4 h-12 lg:w-80 focus-within:border-[#2563EB]">
          <Search size={16} className="text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…" data-testid="catalog-search-input" className="field-bare ml-2 text-base text-slate-900 outline-none w-full" />
        </form>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <Chip active={!line} onClick={() => set("line", "")} testid="filter-line-all">Todas</Chip>
          {Object.entries(LINES).map(([k, v]) => <Chip key={k} active={line === k} onClick={() => set("line", k)} testid={`filter-line-${k}`}>{v}</Chip>)}
        </div>
        <div className="lg:ml-auto w-full lg:w-56">
          <Select value={sort} onValueChange={(v) => set("sort", v === "default" ? "" : v)}>
            <SelectTrigger className="rounded-full h-11" data-testid="catalog-sort-select"><SelectValue placeholder="Ordenar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default" data-testid="sort-default">Destacados</SelectItem>
              <SelectItem value="price_asc" data-testid="sort-price-asc">Precio: menor a mayor</SelectItem>
              <SelectItem value="price_desc" data-testid="sort-price-desc">Precio: mayor a menor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        <span className="text-sm text-slate-500 self-center mr-1 whitespace-nowrap">Por necesidad:</span>
        <Chip active={!need} onClick={() => set("need", "")} testid="filter-need-all">Todas</Chip>
        {Object.entries(NEEDS).map(([k, v]) => <Chip key={k} active={need === k} onClick={() => set("need", k)} testid={`filter-need-${k}`}>{v}</Chip>)}
      </div>

      {products === null ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mt-10">{[...Array(8)].map((_, i) => <div key={i} className="h-96 bg-slate-100 animate-pulse rounded" />)}</div>
      ) : products.length === 0 ? (
        <p className="mt-16 text-center text-slate-500" data-testid="catalog-empty">No hay productos que coincidan con tu búsqueda.</p>
      ) : (
        <>
          <p className="text-sm text-slate-500 mt-8" data-testid="catalog-count">{products.length} productos</p>
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mt-4">
            {products.map((p) => <ProductCard key={p.slug} p={p} />)}
          </div>
        </>
      )}
    </div>
  );
}
